import React from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import { WelcomeErrorCode } from '../../../../../../../types';
import { PASSWORD_LEAKED_LINK } from '../../../../../../../components/links';

import messages from './WelcomeFormErrors.messages';

type WelcomeFormErrorProps = {
  error: WelcomeErrorCode | null | undefined;
  hintError?: string | null | undefined;
};

const invalidPasswordTradukiDescription = 'Reason why a password is invalid';

const errorWithAdvice = defineMessage({
  id: 'welcome.error.adviceWrapper',
  defaultMessage:
    '{errorDescriptionSentence} Avoid common phrases and predictable sequences, and use a mix of characters.',
  description: 'Error describing why a password is invalid, followed by advice on how to fix it',
});

export const WelcomeFormErrorsExperimental = ({ error, hintError }: WelcomeFormErrorProps) => {
  const { formatMessage } = useIntl();
  const withAdvice = (errorDescriptionSentence: ReturnType<typeof formatMessage>) => (
    <>{formatMessage(errorWithAdvice, { errorDescriptionSentence })}</>
  );
  const welcomeError = hintError ?? error;

  switch (welcomeError) {
    case 'passwordStrengthCantMatchDisplayName':
    case 'passwordMatchesDisplayNameError':
      return withAdvice(
        formatMessage(
          {
            id: 'welcome.error.passwordMatchesDisplayName',
            defaultMessage: "Your password can't match your <strong>display name</strong>.",
            description: invalidPasswordTradukiDescription,
          },
          {
            strong: chunks => <strong>{chunks}</strong>,
          }
        )
      );
    case 'passwordStrengthCantMatchAtlassianKeyword':
    case 'passwordMatchesAtlassianKeywordError':
      return withAdvice(
        formatMessage(
          {
            id: 'welcome.error.passwordMatchesAtlassianKeyword',
            defaultMessage: "Your password can't match an <strong>Atlassian keyword</strong>.",
            description: invalidPasswordTradukiDescription,
          },
          {
            strong: chunks => <strong>{chunks}</strong>,
          }
        )
      );
    case 'passwordStrengthCantMatchEmail':
    case 'passwordMatchesEmailError':
      return withAdvice(
        formatMessage(
          {
            id: 'welcome.error.passwordMatchesEmail',
            defaultMessage: "Your password can't match your <strong>email address</strong>.",
            description: invalidPasswordTradukiDescription,
          },
          {
            strong: chunks => <strong>{chunks}</strong>,
          }
        )
      );
    case 'unknown':
      return (
        <FormattedMessage
          id="welcome.error.unknown"
          defaultMessage="Something went wrong while attempting to sign up. Please try again."
        />
      );
    case 'captcha':
      return (
        <FormattedMessage
          id="welcome.error.captcha"
          defaultMessage="We are having trouble verifying reCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support."
        />
      );
    case 'captchaScoreTooLow':
      return (
        <FormattedMessage
          id="welcome.error.captcha.score_too_low"
          defaultMessage="We'd like to make sure it's really you creating your account. Please confirm you're not a robot."
          description="Message asking user to tick the reCAPTCHA checkbox and resubmit the form"
        />
      );
    case 'request_error':
      return (
        <FormattedMessage
          id="welcome.error.request_error"
          defaultMessage="Unable to connect. Check your internet connection or the network settings for your browser and try again."
        />
      );
    case 'passwordLeaked':
      return (
        <FormattedMessage
          {...messages.passwordLeakedXML}
          tagName="p"
          values={{
            'password-leaked-link': chunks => <a href={PASSWORD_LEAKED_LINK}>{chunks}</a>,
          }}
        />
      );
    case 'passwordCommon':
      return withAdvice(
        formatMessage(
          {
            id: 'welcome.error.passwordIsCommon',
            defaultMessage: 'This is a <strong>common password</strong>.',
            description: invalidPasswordTradukiDescription,
          },
          {
            strong: chunks => <strong>{chunks}</strong>,
          }
        )
      );
    case 'invalidPassword':
      return (
        <FormattedMessage id="welcome.error.invalidPassword" defaultMessage="Invalid password" />
      );
    case 'passwordCaseError':
      return (
        <FormattedMessage
          id="welcome.error.passwordCaseError"
          defaultMessage="Your password must contain at least one uppercase and one lowercase letter."
        />
      );
    case 'passwordDigitOrSpecialError':
      return (
        <FormattedMessage
          id="welcome.error.passwordDigitOrSpecialError"
          defaultMessage="Your password must contain at least one digit or special character."
        />
      );
    case 'passwordLengthError':
      return (
        <FormattedMessage
          id="welcome.error.passwordLengthError"
          defaultMessage="Your password must be between 8 and 72 characters long."
        />
      );
    case 'passwordUsedBefore':
      return (
        <FormattedMessage
          id="welcome.error.passwordUsedBefore"
          defaultMessage="You've used that password before. Please choose a different one."
        />
      );
    case 'usernameMustBeEmail':
      return (
        <FormattedMessage
          id="welcome.error.usernameMustBeEmail"
          defaultMessage="Your account username must be a valid email address."
        />
      );
    case 'passwordStrengthErrorWeak':
      return withAdvice(
        <FormattedMessage
          id="welcome.error.bold.passwordStrengthErrorWeak"
          defaultMessage="Your password must have a minimum strength of <strong>weak</strong>."
          values={{
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      );
    case 'passwordStrengthErrorFair':
      return withAdvice(
        <FormattedMessage
          id="welcome.error.bold.passwordStrengthErrorFair"
          defaultMessage="Your password must have a minimum strength of <strong>fair</strong>."
          values={{
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      );
    case 'passwordStrengthErrorGood':
      return withAdvice(
        <FormattedMessage
          id="welcome.error.bold.passwordStrengthErrorGood"
          defaultMessage="Your password must have a minimum strength of <strong>good</strong>."
          values={{
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      );
    case 'passwordStrengthErrorStrong':
      return withAdvice(
        <FormattedMessage
          id="welcome.error.bold.passwordStrengthErrorStrong"
          defaultMessage="Your password must have a minimum strength of <strong>strong</strong>."
          values={{
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      );
    case 'passwordStrengthErrorVeryStrong':
      return withAdvice(
        <FormattedMessage
          id="welcome.error.bold.passwordStrengthErrorVeryStrong"
          defaultMessage="Your password must have a minimum strength of <strong>very strong</strong>."
          values={{
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      );
    default:
      return (
        <FormattedMessage
          id="welcome.form.random.error"
          defaultMessage="An error occurred while attempting to sign up: {error}. Please try again or reach out to Atlassian Support."
          values={{ error }}
        />
      );
  }
};
